/**
 * Keyboard → actions.
 *
 * Held keys repeat on the *game's* schedule, not the operating system's. The
 * OS auto-repeat has a delay before it starts and a rate that varies by
 * machine, which would make walking feel different on every computer; instead
 * the game tracks which keys are down and asks this every frame what the
 * player currently wants.
 *
 * The most recently pressed direction wins. Holding east and then also
 * pressing north should walk north — that is what the hand just did — rather
 * than keeping east because it got there first.
 *
 * Commands are the other half (M0-06). Opening the chat line or stepping a
 * look cursor must happen **once per press**: holding `x` should not fire
 * eighty examines a second, and the held-key model that makes walking feel
 * right is exactly wrong for them. So presses also land in a queue the game
 * drains each frame, and `current()` only ever reports held actions.
 */

import { isHeld, type Action, type Keymap } from './keymap.js';

export class Input {
  private keymap: Keymap;

  /** Codes currently held, oldest first. */
  private readonly held: string[] = [];

  /** Presses since the last drain, in order. */
  private commands: Action[] = [];

  /**
   * While suspended the keyboard belongs to something else — the chat line.
   * Held keys are dropped rather than remembered, so a player who walks into
   * the chat box does not walk back out of it when they close it.
   */
  private suspended = false;

  private attached: (() => void) | null = null;

  constructor(keymap: Keymap) {
    this.keymap = keymap;
  }

  setKeymap(keymap: Keymap): void {
    this.keymap = keymap;
    // Anything no longer bound stops being held, so a rebind mid-walk cannot
    // leave a phantom key down forever.
    for (let i = this.held.length - 1; i >= 0; i -= 1) {
      const code = this.held[i] as string;
      if (!this.keymap[code]) this.held.splice(i, 1);
    }
  }

  /**
   * Is the keyboard the game's right now?
   *
   * Suspending is how the chat line takes the keyboard without the game having
   * to guess from focus events. `attach` also ignores anything typed into a
   * form field, which covers the case where focus moved somewhere nobody told
   * us about.
   */
  get enabled(): boolean {
    return !this.suspended;
  }

  setEnabled(enabled: boolean): void {
    this.suspended = !enabled;
    if (this.suspended) this.releaseAll();
  }

  /** What the player is asking for right now, or `null` for nothing. */
  current(): Action | null {
    for (let i = this.held.length - 1; i >= 0; i -= 1) {
      const action = this.keymap[this.held[i] as string];
      if (action && isHeld(action)) return action;
    }
    return null;
  }

  /**
   * Everything pressed since the last call, oldest first — including moves,
   * which look mode steers the cursor with.
   *
   * Drained rather than peeked: a command read twice is a chat line that opens
   * and immediately closes again.
   */
  takeCommands(): Action[] {
    const commands = this.commands;
    this.commands = [];
    return commands;
  }

  press(code: string): void {
    const action = this.keymap[code];
    if (!action) return;

    if (this.held.includes(code)) return;
    this.commands.push(action);

    // Only held actions go on the stack; a one-shot left there would keep
    // being reported by `current()` for as long as the finger stayed down.
    if (isHeld(action)) this.held.push(code);
  }

  release(code: string): void {
    const index = this.held.indexOf(code);
    if (index >= 0) this.held.splice(index, 1);
  }

  /** Everything let go — what a lost window focus means. */
  releaseAll(): void {
    this.held.length = 0;
    this.commands.length = 0;
  }

  attach(target: Window): void {
    if (this.attached) return;

    const onKeyDown = (event: KeyboardEvent) => {
      // Leave modified keys to the browser: ctrl-L is "reload", not "east".
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (this.suspended || isTyping(event.target)) return;
      if (!this.keymap[event.code]) return;

      event.preventDefault();
      this.press(event.code);
    };

    const onKeyUp = (event: KeyboardEvent) => this.release(event.code);

    // Alt-tabbing away mid-stride must not leave the character walking.
    const onBlur = () => this.releaseAll();

    target.addEventListener('keydown', onKeyDown);
    target.addEventListener('keyup', onKeyUp);
    target.addEventListener('blur', onBlur);

    this.attached = () => {
      target.removeEventListener('keydown', onKeyDown);
      target.removeEventListener('keyup', onKeyUp);
      target.removeEventListener('blur', onBlur);
    };
  }

  detach(): void {
    this.attached?.();
    this.attached = null;
    this.releaseAll();
  }
}

/**
 * Is this keystroke somebody typing into a field?
 *
 * A belt-and-braces companion to `setEnabled`: whatever grabs focus in a later
 * milestone — a search box, a name field at character creation — must not also
 * walk the character across the valley.
 */
function isTyping(target: EventTarget | null): boolean {
  if (target === null || typeof target !== 'object') return false;
  const element = target as { tagName?: unknown; isContentEditable?: unknown };
  if (element.isContentEditable === true) return true;

  const tag = typeof element.tagName === 'string' ? element.tagName.toUpperCase() : '';
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}
